import { FC, useMemo } from 'react';
import { Tag } from '@carbon/react';
import { Pagination } from 'components/pagination';
import { ColumnDef, Row } from '@tanstack/react-table';
import { useAtom } from 'jotai';
import { DataTable } from 'components/data-table';
import { FormattedCount } from 'components/formatted-count';
import { BatchDocumentsTable } from '../batch-documents-table';
import { batchSubmissionsAtom, submissionFilterAtom } from '../atoms';
import {
  submissionStatusColors,
  type ProvisioningBatchSubmission,
} from '../types';
import styles from '../page.module.scss';

export const SubmissionsTable: FC<{ batchId: number }> = ({ batchId }) => {
  const [submissionsLoadable] = useAtom(batchSubmissionsAtom);
  const [submissionFilters, setSubmissionFilters] =
    useAtom(submissionFilterAtom);

  const columns = useMemo<ColumnDef<ProvisioningBatchSubmission>[]>(
    () => [
      {
        id: 'id',
        accessorKey: 'id',
        header: 'Batch ID',
        size: 60,
        cell: (info) => (
          <span className={styles.batchId}>{String(info.getValue())}</span>
        ),
      },
      {
        id: 'providerBatchId',
        accessorKey: 'providerBatchId',
        header: 'Provider Batch ID',
        size: 280,
        cell: (info) => (
          <span className={styles.batchId}>
            {(info.getValue() as string) || (
              <span className={styles.muted}>Pending</span>
            )}
          </span>
        ),
      },
      {
        id: 'status',
        accessorKey: 'status',
        header: 'Status',
        size: 110,
        cell: (info) => {
          const status = info.getValue() as string;
          return (
            <Tag type={submissionStatusColors[status] || 'gray'} size="sm">
              {status}
            </Tag>
          );
        },
      },
      {
        id: 'documentCount',
        accessorKey: 'documentCount',
        header: 'Docs',
        size: 80,
        cell: (info) => <FormattedCount value={info.getValue() as number} />,
      },
      {
        id: 'completedCount',
        accessorKey: 'completedCount',
        header: 'Completed',
        size: 115,
        cell: (info) => <FormattedCount value={info.getValue() as number} />,
      },
      {
        id: 'failedCount',
        accessorKey: 'failedCount',
        header: 'Failed',
        size: 80,
        cell: (info) => {
          const count = info.getValue() as number;
          return count > 0 ? (
            <span className={styles.failedCount}>
              <FormattedCount value={count} />
            </span>
          ) : (
            <span className={styles.zeroCount}>0</span>
          );
        },
      },
      {
        id: 'submittedAt',
        accessorKey: 'submittedAt',
        header: 'Created At',
        size: 170,
        meta: { type: 'datetime' },
      },
      {
        id: 'completedAt',
        accessorKey: 'completedAt',
        header: 'Completed At',
        size: 170,
        meta: { type: 'datetime' },
      },
    ],
    []
  );

  const submissions = useMemo(() => {
    if (submissionsLoadable.state !== 'hasData' || !submissionsLoadable.data)
      return [];
    return submissionsLoadable.data.submissions || [];
  }, [submissionsLoadable]);

  const isLoading = submissionsLoadable.state === 'loading';

  return (
    <div className={styles.tableContainer}>
      <DataTable
        data={submissions}
        columns={columns}
        getRowId={(row) => String(row.id)}
        isLoading={isLoading}
        persistenceKey="provisioning-submissions-table"
        config={{
          columnResizing: true,
          columnReordering: true,
          columnPinning: true,
          sorting: true,
          columnVisibility: true,
          globalSearch: true,
          columnFilters: false,
        }}
        onGlobalSearchChange={(s) => {
          if (s.globalFilter !== submissionFilters.search) {
            setSubmissionFilters({
              ...submissionFilters,
              search: s.globalFilter,
              page: 1,
            });
          }
        }}
        renderRowSubComponent={(row: Row<ProvisioningBatchSubmission>) => (
          <div className={styles.expandedRowWrapper}>
            <BatchDocumentsTable
              batchId={batchId}
              submissionId={row.original.id}
            />
          </div>
        )}
        getRowCanExpand={(row) => row.original.documentCount > 0}
      />

      <Pagination
        page={submissionFilters.page}
        pageSize={submissionFilters.perPage}
        pageSizes={[25, 50, 100]}
        totalItems={
          submissionsLoadable.state === 'hasData'
            ? (submissionsLoadable.data?.pagination.totalCount ?? 0)
            : 0
        }
        onChange={({ page, pageSize }: { page: number; pageSize: number }) => {
          setSubmissionFilters({
            ...submissionFilters,
            page,
            perPage: pageSize,
          });
        }}
      />
    </div>
  );
};
